import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  TableSortLabel,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Tabs,
  Tab,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  RefreshCcw as RefreshIcon,
  PhoneCall as PhoneCallIcon,
  PhoneIncoming as PhoneIncomingIcon,
  PhoneOutgoing as PhoneOutgoingIcon,
  PlayCircle as PlayCircleIcon,
  AlertTriangle as AlertTriangleIcon,
  Clock as ClockIcon,
  MessageCircle as MessageCircleIcon,
  Mail as MailIcon,
  Timer as TimerIcon,
  Download as DownloadIcon,
  Sparkles as SparklesIcon,
  Bot as BotIcon,
  PhoneCall as PhoneCallLogoIcon
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useJob } from '../../context/JobContext';
import { useAuth } from '../../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const translateStatus = (status) => {
  const map = {
    'answered': 'Risposta',
    'missed': 'Persa',
    'busy': 'Occupato',
    'failed': 'Fallita',
    'no_answer': 'Non Risposta'
  };
  return map[status] || status;
};

const translateDirection = (direction) => {
  const map = {
    'inbound': 'Entrata',
    'outbound': 'Uscita'
  };
  return map[direction] || direction;
};

const StatusChip = ({ status }) => {
  let color = 'default';
  switch (status) {
    case 'answered': color = 'success'; break;
    case 'missed': color = 'error'; break;
    case 'busy': color = 'warning'; break;
    case 'failed': color = 'error'; break;
    case 'no_answer': color = 'warning'; break;
    default: color = 'default';
  }
  
  return (
    <Chip 
      label={translateStatus(status)} 
      color={color} 
      size="small" 
      variant="outlined" 
      sx={{ textTransform: 'capitalize' }}
    />
  );
};

const DirectionIcon = ({ direction }) => {
  if (direction === 'inbound') return <PhoneIncomingIcon size={16} color="green" />;
  if (direction === 'outbound') return <PhoneOutgoingIcon size={16} color="blue" />;
  return <PhoneCallIcon size={16} />;
};

const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  
  return parts.join(' ');
};

const formatDurationItLong = (seconds) => {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total < 0) return '-';
  if (total === 0) return '0 secondi';

  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);

  const parts = [];
  if (h > 0) parts.push(`${h} ${h === 1 ? 'ora' : 'ore'}`);
  if (m > 0) parts.push(`${m} ${m === 1 ? 'minuto' : 'minuti'}`);
  if (s > 0) parts.push(`${s} ${s === 1 ? 'secondo' : 'secondi'}`);

  return parts.join(' e ');
};

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={0} variant="outlined">
    <Box sx={{ 
      p: 1.5, 
      borderRadius: '50%', 
      bgcolor: `${color}15`, 
      color: color,
      mr: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  try {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  } catch (e) {
    return value;
  }
};

const isValidEmail = (value) => {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const getPhoneSearchCandidates = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return [];
  const digits = raw.replace(/[^\d]/g, '');

  const candidates = [raw];
  if (digits) {
    candidates.push(digits);
    if (digits.startsWith('39') && digits.length > 2) {
      candidates.push(digits.slice(2));
      candidates.push(`+${digits}`);
      candidates.push(`+39${digits.slice(2)}`);
    } else {
      candidates.push(`+${digits}`);
      candidates.push(`+39${digits}`);
    }
    if (digits.length >= 7) candidates.push(digits.slice(-7));
    if (digits.length >= 8) candidates.push(digits.slice(-8));
    if (digits.length >= 9) candidates.push(digits.slice(-9));
  }

  return Array.from(new Set(candidates.map((c) => String(c).trim()).filter(Boolean)));
};

const escapeHtml = (text) => {
  const s = String(text ?? '');
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const textToQuillHtml = (text) => {
  const raw = String(text ?? '').replace(/\r\n/g, '\n');
  const lines = raw.split('\n');
  const html = lines.map((l) => `<p>${escapeHtml(l)}</p>`).join('');
  return html || '<p><br></p>';
};

const isQuillEmpty = (html) => {
  const s = String(html || '').trim();
  if (!s) return true;
  return s === '<p><br></p>' || s === '<p></p>' || s === '<br>' || s === '<br/>' || s === '<br />';
};

const appendTextToQuill = (currentHtml, textToAppend) => {
  const addition = textToQuillHtml(textToAppend);
  if (isQuillEmpty(currentHtml)) return addition;
  const base = String(currentHtml || '');
  return `${base}<p><br></p>${addition}`;
};

const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
  ]
};

const quillFormats = [
  'font',
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'script',
  'align',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'code-block',
  'link',
  'image'
];

const CloudTalkList = () => {
  const DEFAULT_LEGAL_OFFICE_EMAIL = 'ufficio_legale@debitobancario.it';
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({
    total_calls: 0,
    avg_handling_time: 0,
    total_talk_time: 0,
    avg_talk_time: 0
  });
  const { addJob, jobs } = useJob();
  const { user } = useAuth();
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('started_at');
  const [orderDirection, setOrderDirection] = useState('desc');

  // Filters state
  const getTodayYmd = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [filters, setFilters] = useState(() => {
    const today = getTodayYmd();
    return {
      date_from: today,
      date_to: today,
      status: '',
      direction: ''
    };
  });

  // Sync Dialog State
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncMode, setSyncMode] = useState('last_sync');
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingCall, setRecordingCall] = useState(null);
  const [intelligenceDialogOpen, setIntelligenceDialogOpen] = useState(false);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [intelligenceError, setIntelligenceError] = useState(null);
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [intelligenceCall, setIntelligenceCall] = useState(null);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [privacyPendingCall, setPrivacyPendingCall] = useState(null);
  const [agentOptions, setAgentOptions] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportAgentId, setReportAgentId] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportFormat, setReportFormat] = useState('pdf');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailCall, setEmailCall] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailAccountId, setEmailAccountId] = useState('');
  const [emailSummaryLoading, setEmailSummaryLoading] = useState(false);
  const [emailDraftLoading, setEmailDraftLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    subject: '',
    body: '<p><br></p>'
  });

  const formatLastSyncAt = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const apiFilters = { ...filters };
      // If date_to is set, ensure it covers the whole day
      if (apiFilters.date_to) {
        apiFilters.date_to = `${apiFilters.date_to} 23:59:59`;
      }
      // If date_from is set, ensure it starts at beginning of day (optional, usually implied by date type but good for clarity)
      if (apiFilters.date_from) {
        apiFilters.date_from = `${apiFilters.date_from} 00:00:00`;
      }

      const response = await api.get('/cloudtalk', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          orderBy,
          orderDirection,
          ...apiFilters
        }
      });
      setCalls(response.data.data);
      setTotal(response.data.meta.total);
      if (response.data.stats) {
        setStats(response.data.stats);
      }
      setLastSyncAt(response.data.last_sync_at || null);
    } catch (error) {
      console.error('Error fetching calls:', error);
      toast.error('Errore nel caricamento delle chiamate');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/cloudtalk/agents');
      setAgentOptions(response.data?.agents || []);
    } catch (error) {
      console.error('Error fetching CloudTalk agents:', error);
    }
  };

  const agentSelectOptions = useMemo(() => {
    const map = new Map();
    agentOptions.forEach((a) => {
      const rawName = a.agent_name || a.name || '(unknown)';
      const name = (rawName || '').trim() || '(unknown)';
      const key = name.toLowerCase();
      const newId = a.agent_id ? String(a.agent_id) : '';

      if (!map.has(key)) {
        map.set(key, {
          id: newId,
          label: name
        });
      } else if (newId) {
        const existing = map.get(key);
        if (!existing.id) {
          map.set(key, {
            id: newId,
            label: name
          });
        }
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'it', { sensitivity: 'base' })
    );
  }, [agentOptions]);

  const closeRecordingDialog = () => {
    setRecordingDialogOpen(false);
    setRecordingLoading(false);
    setRecordingError(null);
    setRecordingCall(null);
    if (recordingUrl) {
      window.URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }
  };

  const parseBlobError = async (data) => {
    try {
      if (!data) return null;
      if (typeof data === 'string') return data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          return parsed?.responseData?.message || parsed?.message || text;
        } catch (_) {
          return text;
        }
      }
      if (typeof data === 'object') {
        return data?.responseData?.message || data?.message || null;
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  const openRecordingDialog = async (call) => {
    setRecordingCall(call);
    setRecordingDialogOpen(true);
    setRecordingLoading(true);
    setRecordingError(null);
    if (recordingUrl) {
      window.URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }

    try {
      const response = await api.get(`/cloudtalk/recording/${call.call_id}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'audio/x-wav'
      });
      const url = window.URL.createObjectURL(blob);
      setRecordingUrl(url);
    } catch (error) {
      const message = await parseBlobError(error?.response?.data);
      setRecordingError(message || 'Registrazione non disponibile');
    } finally {
      setRecordingLoading(false);
    }
  };

  const closeIntelligenceDialog = () => {
    setIntelligenceDialogOpen(false);
    setIntelligenceLoading(false);
    setIntelligenceError(null);
    setIntelligenceData(null);
    setIntelligenceCall(null);
  };

  const extractKeywordLabel = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      return item.topic || item.label || item.name || item.keyword || JSON.stringify(item);
    }
    return String(item);
  };

  const openIntelligenceDialog = async (call) => {
    setIntelligenceCall(call);
    setIntelligenceDialogOpen(true);
    setIntelligenceLoading(true);
    setIntelligenceError(null);
    setIntelligenceData(null);

    try {
      const response = await api.get(`/cloudtalk/${call.id}/intelligence`);
      setIntelligenceData(response.data || null);
    } catch (error) {
      setIntelligenceError(error?.response?.data?.message || 'Errore nel recupero dell’intelligence');
    } finally {
      setIntelligenceLoading(false);
    }
  };

  const openPrivacyDialog = (call) => {
    setPrivacyPendingCall(call);
    setPrivacyDialogOpen(true);
  };

  const closePrivacyDialog = () => {
    setPrivacyDialogOpen(false);
    setPrivacyPendingCall(null);
  };

  const confirmPrivacyDialog = async () => {
    const call = privacyPendingCall;
    setPrivacyDialogOpen(false);
    setPrivacyPendingCall(null);
    if (call) {
      await openRecordingDialog(call);
    }
  };

  const formatPhone = (value) => {
    if (!value) return '-';
    const str = String(value);
    return str.replace(/^(\+39)(\d+)/, '$1 $2');
  };

  const getPrivacyNames = (call) => {
    const direction = call?.direction;
    const agentName = call?.agent_name || null;
    const contactName = call?.contact_name || null;

    if (direction === 'inbound') {
      return { daName: contactName, aName: agentName };
    }
    if (direction === 'outbound') {
      return { daName: agentName, aName: contactName };
    }
    return { daName: null, aName: null };
  };

  const formatParty = (number, name) => {
    const n = formatPhone(number);
    return name ? `${n} (${name})` : n;
  };

  const normalizeKeywords = (keywords) => {
    if (!keywords) return [];
    if (Array.isArray(keywords)) return keywords.map(extractKeywordLabel).filter(Boolean);
    if (typeof keywords === 'string') {
      try {
        const parsed = JSON.parse(keywords);
        return normalizeKeywords(parsed);
      } catch (_) {
        return [keywords];
      }
    }
    if (typeof keywords === 'object') return [extractKeywordLabel(keywords)];
    return [];
  };

  const translateSentiment = (value) => {
    const map = {
      'very negative': 'Molto negativo',
      'negative': 'Negativo',
      'neutral': 'Neutro',
      'positive': 'Positivo',
      'very positive': 'Molto positivo'
    };
    if (!value) return '-';
    const key = String(value).toLowerCase();
    return map[key] || value;
  };

  const translateKeyword = (value) => {
    const map = {
      'legal consultation': 'Consulenza legale',
      'personalized plans': 'Piani personalizzati',
      'payment issues': 'Problemi di pagamento',
      'debt collection': 'Recupero crediti',
      'customer support': 'Supporto clienti',
      'contract discussion': 'Discussione contratto',
      'billing': 'Fatturazione',
      'complaint': 'Reclamo',
      'refund': 'Rimborso',
      'follow up': 'Follow-up'
    };
    const raw = String(value || '').trim();
    const translated = map[raw.toLowerCase()];
    if (!translated) return raw;
    if (translated.toLowerCase() === raw.toLowerCase()) return translated;
    return `${translated} (${raw})`;
  };

  const [summaryLang, setSummaryLang] = useState('en');
  const [intelligenceTab, setIntelligenceTab] = useState('it-summary');

  useEffect(() => {
    fetchCalls();
  }, [page, rowsPerPage, search, orderBy, orderDirection, filters]);

  useEffect(() => {
    fetchAgents();
  }, []);

  // Check if any sync job completed to refresh list
  useEffect(() => {
    const syncJobs = jobs.filter(j => j.type === 'cloudtalk_sync' && j.status === 'completed');
    if (syncJobs.length > 0) {
      const lastJob = syncJobs[syncJobs.length - 1];
      // Simple check to avoid infinite re-fetch loop if we don't track which one we handled
      // But since fetchCalls just updates data, it's fine.
      // Better: check if we have a "new" completed job.
      // For now, let's just re-fetch when jobs change and there is a completed sync job.
      // Ideally we would compare with a ref.
      fetchCalls();
    }
  }, [jobs]);

  const handleSync = async () => {
    if (syncMode === 'custom' && (!syncDateFrom || !syncDateTo)) {
      toast.error('Seleziona entrambe le date per la sincronizzazione personalizzata');
      return;
    }

    setSyncing(true);
    setSyncDialogOpen(false);
    
    try {
      const formatDate = (dateStr, isEnd = false) => {
        if (!dateStr) return undefined;
        // dateStr is YYYY-MM-DD
        // For API we need YYYY-MM-DD HH:mm:ss
        // If isEnd, append 23:59:59, else 00:00:00
        return `${dateStr} ${isEnd ? '23:59:59' : '00:00:00'}`;
      };

      let fromDate = formatDate(syncDateFrom, false);
      let toDate = formatDate(syncDateTo, true);

      const response = await api.post('/cloudtalk/sync', {
        mode: syncMode,
        date_from: syncMode === 'custom' ? fromDate : undefined,
        date_to: syncMode === 'custom' ? toDate : undefined
      });
      
      if (response.data.jobId) {
        addJob(response.data.jobId, 'cloudtalk_sync');
      } else {
        toast.success('Sincronizzazione avviata');
        setTimeout(fetchCalls, 2000); 
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Errore durante la sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchEmailAccounts = async () => {
    try {
      const res = await api.get('/email-accounts', {
        params: {
          page: 1,
          limit: 200,
          is_active: 1
        }
      });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setEmailAccounts(list);
      return list;
    } catch (error) {
      console.error('Error fetching email accounts:', error);
      setEmailAccounts([]);
      return [];
    }
  };

  const pickPreferredEmailAccountId = (accounts) => {
    const userEmail = String(user?.email || '').trim().toLowerCase();
    if (!userEmail) return '';
    const match = (accounts || []).find(
      (a) => String(a?.email || '').trim().toLowerCase() === userEmail
    );
    return match?.id ? String(match.id) : '';
  };

  const lookupCustomerEmailByPhone = async (phone) => {
    const candidates = getPhoneSearchCandidates(phone);
    for (const searchValue of candidates) {
      try {
        const res = await api.get('/customer', {
          params: {
            page: 1,
            limit: 1,
            search: searchValue
          }
        });
        const first = Array.isArray(res.data?.data) ? res.data.data[0] : null;
        const email = first?.email ? String(first.email).trim() : '';
        if (email) return email;
      } catch (_) {}
    }
    return null;
  };

  const fetchIntelligenceSummary = async (call) => {
    if (!call?.id) return null;
    try {
      const res = await api.get(`/cloudtalk/${call.id}/summary`);
      const data = res.data || null;
      const summary = data?.summary_it || data?.summary || null;
      return summary ? String(summary) : null;
    } catch (_) {
      return null;
    }
  };

  const generateEmailDraftFromSummary = async (call) => {
    if (!call) return null;
    const summary = await fetchIntelligenceSummary(call);
    if (!summary) return null;
    const meta = {
      started_at: call.started_at || null,
      direction: call.direction || null,
      from_number: call.from_number || null,
      to_number: call.to_number || null,
      agent_name: call.agent_name || null,
      contact_name: call.contact_name || null
    };
    const res = await api.post('/cloudtalk/email-draft', {
      summary,
      meta
    });
    return res.data || null;
  };

  const openEmailDialog = async (call) => {
    setEmailCall(call || null);
    const startedAt = call?.started_at ? formatDateTimeIt(call.started_at) : '';
    const subjectParts = [
      'Chiamata CloudTalk',
      startedAt ? `(${startedAt})` : null
    ].filter(Boolean);

    const cachedSummary =
      intelligenceCall?.id === call?.id
        ? (intelligenceData?.summary_it || intelligenceData?.summary || '')
        : '';

    setEmailForm({
      to: '',
      cc: '',
      subject: subjectParts.join(' '),
      body: cachedSummary ? textToQuillHtml(String(cachedSummary)) : '<p><br></p>'
    });
    setEmailDialogOpen(true);

    let accounts = emailAccounts;
    if (accounts.length === 0) {
      accounts = await fetchEmailAccounts();
    }
    const preferredAccountId = pickPreferredEmailAccountId(accounts);
    const legalOfficeEmail = String(DEFAULT_LEGAL_OFFICE_EMAIL).trim().toLowerCase();
    const preferredAccount = preferredAccountId
      ? accounts.find((a) => String(a?.id) === String(preferredAccountId))
      : null;
    const fallbackAccount =
      accounts.find((a) => String(a?.email || '').trim().toLowerCase() === legalOfficeEmail) ||
      accounts[0] ||
      null;
    const selectedAccount = preferredAccount || fallbackAccount;

    if (selectedAccount?.id != null) {
      setEmailAccountId(String(selectedAccount.id));
    }

    const selectedEmail = String(selectedAccount?.email || '').trim().toLowerCase();
    const usedFallback = !preferredAccount;
    const ccValue = usedFallback
      ? ''
      : (selectedEmail && selectedEmail === legalOfficeEmail ? '' : DEFAULT_LEGAL_OFFICE_EMAIL);
    setEmailForm((prev) => ({ ...prev, cc: ccValue }));

    const phoneToLookup =
      call?.direction === 'inbound' ? call?.from_number : call?.to_number;
    const foundEmail = await lookupCustomerEmailByPhone(phoneToLookup);
    if (foundEmail) {
      setEmailForm((prev) => ({ ...prev, to: foundEmail }));
      return;
    }
    const directEmail = isValidEmail(call?.contact_email)
      ? String(call.contact_email).trim()
      : '';
    if (directEmail) setEmailForm((prev) => ({ ...prev, to: directEmail }));
  };

  const closeEmailDialog = () => {
    if (emailSending) return;
    setEmailDialogOpen(false);
    setEmailCall(null);
  };

  const handleSendEmail = async () => {
    if (!emailAccountId) {
      toast.error('Seleziona un account email per l’invio');
      return;
    }
    if (!emailForm.to || !emailForm.subject || isQuillEmpty(emailForm.body)) {
      toast.error('Compila i campi obbligatori (A, Oggetto, Messaggio)');
      return;
    }

    setEmailSending(true);
    try {
      const html = String(emailForm.body || '');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const text = (doc.body && doc.body.textContent ? doc.body.textContent : '').trim();

      const formData = new FormData();
      formData.append('to', emailForm.to);
      if (emailForm.cc) formData.append('cc', emailForm.cc);
      formData.append('subject', emailForm.subject);
      formData.append('body', text);
      formData.append('body_html', html);

      await api.post(`/email-accounts/${emailAccountId}/send`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Email inviata con successo');
      closeEmailDialog();
    } catch (error) {
      console.error('Error sending email:', error);
      const data = error?.response?.data || {};
      const traceId =
        data.traceId ||
        data.trace_id ||
        error?.response?.headers?.['x-trace-id'] ||
        error?.response?.headers?.['X-Trace-Id'];
      const code = data.code ? String(data.code) : null;
      const backendMessage = data.message ? String(data.message) : '';
      const technicalMessage = data.error ? String(data.error) : '';
      const status = error?.response?.status;

      const idPart = traceId ? ` (ID: ${traceId})` : '';

      let uiMessage = '';
      if (code === 'SMTP_AUTH_FAILED') {
        uiMessage = `Autenticazione email non riuscita. Verifica credenziali e impostazioni SMTP${idPart}`;
      } else if (code === 'SMTP_CONNECTION_FAILED') {
        uiMessage = `Server di posta non raggiungibile o in timeout. Riprova${idPart}`;
      } else if (code === 'SMTP_RECIPIENT_REJECTED') {
        uiMessage = `Destinatario non valido o rifiutato dal server di posta${idPart}`;
      } else if (backendMessage) {
        uiMessage = `${backendMessage}${idPart}`;
      } else if (status) {
        uiMessage = `Errore ${status} durante l'invio dell'email${idPart}`;
      } else {
        uiMessage = `Errore durante l'invio dell'email${idPart}`;
      }

      const detail = (technicalMessage || '').trim();
      const detailShort =
        detail && detail.length > 180 ? `${detail.slice(0, 177)}...` : detail;
      if (detailShort) {
        uiMessage = `${uiMessage}: ${detailShort}`;
      }

      toast.error(uiMessage);
    } finally {
      setEmailSending(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  const handleReportConfirm = async () => {
    if (!reportAgentId || !reportDateFrom || !reportDateTo) {
      toast.error('Seleziona agente e intervallo date per il report');
      return;
    }

    if (reportFormat === 'pdf') {
      if (reporting) return;
      setReporting(true);
      try {
        const payload = {
          agent_id: reportAgentId,
          date_from: `${reportDateFrom} 00:00:00`,
          date_to: `${reportDateTo} 23:59:59`,
          direction: filters.direction || '',
          status: filters.status || ''
        };
        const res = await api.post('/cloudtalk/report', payload);
        const jobId = res.data && res.data.jobId;
        if (jobId) {
          addJob(jobId, 'cloudtalk_calls');
        }
        toast.success(
          res.data?.message ||
            'Generazione report PDF avviata. Il file sarà disponibile nelle notifiche.'
        );
        setReportDialogOpen(false);
      } catch (error) {
        console.error('Error starting CloudTalk PDF report:', error);
        toast.error('Errore durante l\'avvio del report PDF');
      } finally {
        setReporting(false);
      }
      return;
    }

    if (exporting) return;
    setExporting(true);
    try {
      const apiFilters = {
        ...filters,
        agent_id: reportAgentId,
        date_from: reportDateFrom,
        date_to: reportDateTo
      };

      if (apiFilters.date_to) {
        apiFilters.date_to = `${apiFilters.date_to} 23:59:59`;
      }
      if (apiFilters.date_from) {
        apiFilters.date_from = `${apiFilters.date_from} 00:00:00`;
      }

      const limit = 1000;
      let pageExport = 1;
      const allRows = [];

      for (;;) {
        const response = await api.get('/cloudtalk', {
          params: {
            page: pageExport,
            limit,
            search,
            orderBy,
            orderDirection,
            ...apiFilters
          }
        });
        const rows = response.data?.data || [];
        allRows.push(...rows);
        if (rows.length < limit) break;
        pageExport += 1;
      }

      if (allRows.length === 0) {
        toast.info('Nessuna chiamata trovata per i filtri selezionati');
        return;
      }

      const header = [
        'DataOra',
        'Direzione',
        'Da',
        'A',
        'Stato',
        'Durata',
        'Agente',
        'Contatto',
        'CallId'
      ];

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (/[",;\n]/.test(str)) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const lines = [];
      lines.push(header.join(';'));

      allRows.forEach((call) => {
        const row = [
          formatDateTimeIt(call.started_at),
          translateDirection(call.direction),
          call.from_number || '',
          call.to_number || '',
          translateStatus(call.status),
          formatDurationItLong(call.duration),
          call.agent_name || '',
          call.contact_name || '',
          call.call_id || ''
        ].map(escapeCsv);
        lines.push(row.join(';'));
      });

      const csvContent = lines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(
        2,
        '0'
      )}${String(now.getDate()).padStart(2, '0')}_${String(
        now.getHours()
      ).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      link.href = url;
      link.download = `cloudtalk_chiamate_${ts}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting calls:', error);
      toast.error("Errore durante l'esportazione delle chiamate");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PhoneCallLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              CloudTalk - Chiamate
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Storico e analisi delle chiamate telefoniche
            </Typography>
          </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon size={18} />}
              onClick={() => {
                const today = getTodayYmd();
                setReportAgentId(filters.agent_id || '');
                setReportDateFrom(filters.date_from || today);
                setReportDateTo(filters.date_to || today);
                setReportFormat('pdf');
                setReportDialogOpen(true);
              }}
            >
              Report
            </Button>
            <Button 
              variant="contained" 
              startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon size={20} />}
              onClick={() => setSyncDialogOpen(true)}
              disabled={syncing}
            >
              {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
            </Button>
          </Box>
      </Box>

      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Report chiamate CloudTalk</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              size="small"
              options={agentSelectOptions}
              value={
                agentSelectOptions.find(
                  (o) => o.id && o.id === String(reportAgentId)
                ) || null
              }
              autoHighlight
              onChange={(event, newValue) =>
                setReportAgentId(newValue ? newValue.id : '')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Agente"
                  placeholder="Seleziona agente"
                />
              )}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Data inizio"
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Data fine"
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
              />
            </Box>
            <FormControl component="fieldset">
              <FormLabel component="legend">Formato</FormLabel>
              <RadioGroup
                row
                value={reportFormat}
                onChange={(e) => setReportFormat(e.target.value)}
              >
                <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
                <FormControlLabel value="csv" control={<Radio />} label="CSV" />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Annulla</Button>
          <Button
            onClick={handleReportConfirm}
            disabled={reporting || exporting}
          >
            {reportFormat === 'pdf'
              ? reporting
                ? 'Report PDF...'
                : 'Genera PDF'
              : exporting
              ? 'Esporta CSV...'
              : 'Esporta CSV'}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget 
            title="Tempo medio gestione" 
            value={formatDuration(stats.avg_handling_time)} 
            icon={ClockIcon} 
            color="#0ea5e9" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget 
            title="Totale chiamate" 
            value={stats.total_calls} 
            icon={PhoneCallIcon} 
            color="#6366f1" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget 
            title="Tempo totale conversazione" 
            value={formatDuration(stats.total_talk_time)} 
            icon={MessageCircleIcon} 
            color="#22c55e" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget 
            title="Durata media conversazione" 
            value={formatDuration(stats.avg_talk_time)} 
            icon={TimerIcon} 
            color="#f59e0b" 
          />
        </Grid>
      </Grid>

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                placeholder="Cerca per ID, numero, agente..."
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato</InputLabel>
                <Select
                  value={filters.status}
                  label="Stato"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="answered">Risposta</MenuItem>
                  <MenuItem value="missed">Persa</MenuItem>
                  <MenuItem value="busy">Occupato</MenuItem>
                  <MenuItem value="failed">Fallita</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Direzione</InputLabel>
                <Select
                  value={filters.direction}
                  label="Direzione"
                  onChange={(e) => setFilters(prev => ({ ...prev, direction: e.target.value }))}
                >
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="inbound">Entrata</MenuItem>
                  <MenuItem value="outbound">Uscita</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Da"
                InputLabelProps={{ shrink: true }}
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label="A"
                InputLabelProps={{ shrink: true }}
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'started_at'}
                      direction={orderBy === 'started_at' ? orderDirection : 'asc'}
                      onClick={createSortHandler('started_at')}
                    >
                      Data/Ora
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'direction'}
                      direction={orderBy === 'direction' ? orderDirection : 'asc'}
                      onClick={createSortHandler('direction')}
                    >
                      Direzione
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Da</TableCell>
                  <TableCell>A</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? orderDirection : 'asc'}
                      onClick={createSortHandler('status')}
                    >
                      Stato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'duration'}
                      direction={orderBy === 'duration' ? orderDirection : 'asc'}
                      onClick={createSortHandler('duration')}
                    >
                      Durata
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'agent_name'}
                      direction={orderBy === 'agent_name' ? orderDirection : 'asc'}
                      onClick={createSortHandler('agent_name')}
                    >
                      Agente
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'contact_name'}
                      direction={orderBy === 'contact_name' ? orderDirection : 'asc'}
                      onClick={createSortHandler('contact_name')}
                    >
                      Contatto
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : calls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      Nessuna chiamata trovata
                    </TableCell>
                  </TableRow>
                ) : (
                  calls.map((call) => (
                  <TableRow key={call.id} hover>
                    <TableCell>{formatDateTimeIt(call.started_at)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionIcon direction={call.direction} />
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {translateDirection(call.direction)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{call.from_number}</TableCell>
                      <TableCell>{call.to_number}</TableCell>
                      <TableCell><StatusChip status={call.status} /></TableCell>
                      <TableCell>{formatDurationItLong(call.duration)}</TableCell>
                      <TableCell>{call.agent_name || '-'}</TableCell>
                      <TableCell>{call.contact_name || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color="default"
                            title="Invia email"
                            onClick={() => openEmailDialog(call)}
                          >
                            <MailIcon size={18} />
                          </IconButton>
                          {Boolean(call.call_id) && (
                            <IconButton
                              size="small"
                              color="secondary"
                              title="Visualizza intelligence"
                              onClick={() => openIntelligenceDialog(call)}
                            >
                              <MessageCircleIcon size={18} />
                            </IconButton>
                          )}
                          {Boolean(call.has_recording) && Boolean(call.call_id) && (
                            <IconButton 
                              size="small" 
                              color="primary" 
                              title="Ascolta registrazione"
                              onClick={() => openPrivacyDialog(call)}
                            >
                              <PlayCircleIcon size={20} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={emailDialogOpen} onClose={closeEmailDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MailIcon size={18} color="#2563eb" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Invio email
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Compila i campi e invia il riepilogo della chiamata
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              size="small"
              options={emailAccounts}
              value={emailAccounts.find((a) => String(a.id) === String(emailAccountId)) || null}
              autoHighlight
              noOptionsText="Nessun account disponibile"
              getOptionLabel={(acc) =>
                (acc?.name || acc?.email)
                  ? `${acc.name || acc.email}${acc.name ? ` <${acc.email}>` : ''}`
                  : `Account #${acc?.id}`
              }
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              onOpen={() => {
                if (emailAccounts.length === 0) fetchEmailAccounts();
              }}
              onChange={(event, newValue) => setEmailAccountId(newValue?.id ? String(newValue.id) : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Account mittente"
                  placeholder="Cerca per nome o email"
                />
              )}
            />
            <TextField
              size="small"
              label="A"
              placeholder="es. nome@dominio.it"
              value={emailForm.to}
              onChange={(e) => setEmailForm((prev) => ({ ...prev, to: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="CC"
              placeholder="es. nome@dominio.it"
              value={emailForm.cc}
              onChange={(e) => setEmailForm((prev) => ({ ...prev, cc: e.target.value }))}
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                label="Oggetto"
                value={emailForm.subject}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
                fullWidth
              />
              <Tooltip title="Inserisci la sintesi operativa (IT) nel messaggio">
                <span>
                  <IconButton
                    size="small"
                    disabled={emailSummaryLoading || !emailCall?.call_id}
                    onClick={async () => {
                      if (!emailCall) return;
                      if (emailSummaryLoading) return;
                      setEmailSummaryLoading(true);
                      try {
                        const summary = await fetchIntelligenceSummary(emailCall);
                        if (!summary) {
                          toast.info('Sintesi operativa non disponibile per questa chiamata');
                          return;
                        }
                        setEmailForm((prev) => ({
                          ...prev,
                          body: appendTextToQuill(prev.body, summary)
                        }));
                      } finally {
                        setEmailSummaryLoading(false);
                      }
                    }}
                  >
                    {emailSummaryLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SparklesIcon size={18} color="#4f46e5" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Genera bozza email (AI)">
                <span>
                  <IconButton
                    size="small"
                    disabled={emailDraftLoading || !emailCall?.call_id}
                    onClick={async () => {
                      if (!emailCall) return;
                      if (emailDraftLoading) return;
                      setEmailDraftLoading(true);
                      try {
                        const draft = await generateEmailDraftFromSummary(emailCall);
                        if (!draft || (!draft.subject && !draft.body_html)) {
                          toast.info('Bozza email non disponibile per questa chiamata');
                          return;
                        }
                        setEmailForm((prev) => ({
                          ...prev,
                          subject: draft.subject ? String(draft.subject) : prev.subject,
                          body: draft.body_html ? String(draft.body_html) : prev.body
                        }));
                      } catch (e) {
                        console.error('Email draft error:', e);
                        const data = e?.response?.data;
                        let message = null;
                        if (data && typeof data === 'object') {
                          const serverMessage = data.message ? String(data.message) : '';
                          const serverError = data.error ? String(data.error) : '';
                          if (serverError && (!serverMessage || serverMessage.toLowerCase().startsWith('errore durante la generazione'))) {
                            message = serverError;
                          } else if (serverMessage && serverError) {
                            message = `${serverMessage}: ${serverError}`;
                          } else {
                            message = serverMessage || serverError || null;
                          }
                        } else if (typeof data === 'string') {
                          message = data;
                        } else if (e?.message) {
                          message = String(e.message);
                        } else {
                          message = String(e || '');
                        }
                        toast.error(message || 'Errore durante la generazione della bozza email');
                      } finally {
                        setEmailDraftLoading(false);
                      }
                    }}
                  >
                    {emailDraftLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <BotIcon size={18} color="#0ea5e9" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Messaggio
              </Typography>
              <Box sx={{ '& .ql-container': { minHeight: 260 } }}>
                <ReactQuill
                  theme="snow"
                  value={emailForm.body}
                  onChange={(value) => setEmailForm((prev) => ({ ...prev, body: value }))}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </Box>
            </Box>
            {emailCall && (
              <Typography variant="caption" color="text.secondary">
                {emailCall.direction === 'inbound' ? 'Da' : 'A'}:{' '}
                {emailCall.direction === 'inbound' ? emailCall.from_number : emailCall.to_number}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailDialog} disabled={emailSending}>
            Annulla
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={emailSending}
            startIcon={emailSending ? <CircularProgress size={18} color="inherit" /> : <MailIcon size={18} />}
          >
            Invia
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sincronizzazione CloudTalk</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Modalità di sincronizzazione</FormLabel>
              <RadioGroup
                value={syncMode}
                onChange={(e) => setSyncMode(e.target.value)}
              >
                <FormControlLabel 
                  value="last_sync" 
                  control={<Radio />} 
                  label={
                    formatLastSyncAt(lastSyncAt)
                      ? `Dall'ultima sincronizzazione ${formatLastSyncAt(lastSyncAt)} (default)`
                      : "Dall'ultima sincronizzazione (default)"
                  } 
                />
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label="Seleziona periodo specifico" 
                />
              </RadioGroup>
            </FormControl>

            {syncMode === 'custom' && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Data Inizio"
                  type="date"
                  value={syncDateFrom}
                  onChange={(e) => setSyncDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Data Fine"
                  type="date"
                  value={syncDateTo}
                  onChange={(e) => setSyncDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Annulla</Button>
          <Button 
            onClick={handleSync} 
            variant="contained"
            disabled={false}
            autoFocus
          >
            Avvia Sincronizzazione
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recordingDialogOpen} onClose={closeRecordingDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Registrazione</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {recordingLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : recordingError ? (
              <Typography color="error">{recordingError}</Typography>
            ) : recordingUrl ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <audio controls src={recordingUrl} style={{ width: '100%' }} />
                <Button
                  variant="outlined"
                  color="primary"
                  component="a"
                  href={recordingUrl}
                  download={recordingCall?.call_id ? `cloudtalk_recording_${recordingCall.call_id}.wav` : 'cloudtalk_recording.wav'}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DownloadIcon size={18} color="#0ea5e9" />
                    <span>Scarica WAV</span>
                  </Box>
                </Button>
              </Box>
            ) : (
              <Typography>Registrazione non disponibile</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecordingDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={intelligenceDialogOpen} onClose={closeIntelligenceDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Intelligence chiamata
          {intelligenceCall && (intelligenceCall.call_id || intelligenceCall.id) && (
            <Typography component="span" variant="subtitle2" sx={{ ml: 1, fontWeight: 500 }}>
              — ID {intelligenceCall.call_id || intelligenceCall.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {intelligenceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : intelligenceError ? (
              <Typography color="error">{intelligenceError}</Typography>
            ) : intelligenceData ? (
              <>
                <Tabs
                  value={intelligenceTab}
                  onChange={(e, v) => setIntelligenceTab(v)}
                  sx={{ mb: 1 }}
                >
                  <Tab value="overview" label="Panoramica" />
                  <Tab value="transcription" label="Trascrizione" />
                  <Tab
                    value="ai-notes"
                    label="Note AI"
                    disabled={!intelligenceData.smartNotes && !intelligenceData.smartNotes_it}
                  />
                  <Tab
                    value="it-summary"
                    icon={<SparklesIcon size={14} color="#4f46e5" />}
                    iconPosition="start"
                    label="Sintesi operativa (IT)"
                  />
                </Tabs>

                {intelligenceTab === 'overview' && (
                  <>
                    {intelligenceData.summary && (
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">Summary</Typography>
                          {intelligenceData.summary_it && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setSummaryLang((prev) => (prev === 'it' ? 'en' : 'it'))}
                              sx={{ textTransform: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                              {summaryLang === 'it' ? (
                                'Mostra originale'
                              ) : (
                                <>
                                  <span style={{ fontSize: 14 }}>🇮🇹</span>
                                  <span>Traduci in italiano</span>
                                </>
                              )}
                            </Button>
                          )}
                        </Box>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {summaryLang === 'it' && intelligenceData.summary_it
                              ? intelligenceData.summary_it
                              : intelligenceData.summary}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Sentiment</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {translateSentiment(intelligenceData.overallSentiment)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Score chiamata</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {intelligenceData.callScore !== null && intelligenceData.callScore !== undefined
                              ? `${Math.round(Number(intelligenceData.callScore))}`
                              : 'Non disponibile'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Parole chiave</Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {normalizeKeywords(intelligenceData.keywords).length > 0
                              ? normalizeKeywords(intelligenceData.keywords).map((kw) => (
                                  <Chip key={kw} size="small" label={translateKeyword(kw)} variant="outlined" />
                                ))
                              : <Typography variant="body2">Non disponibili</Typography>}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                )}

                {intelligenceTab === 'it-summary' && (
                  <Box>
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {intelligenceData.summary_it || intelligenceData.summary || 'Nessun summary disponibile'}
                      </Typography>
                    </Paper>
                    {(() => {
                      const notes = intelligenceData.smartNotes_it || intelligenceData.smartNotes;
                      if (!notes) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Nessuna nota AI disponibile per questa chiamata.
                          </Typography>
                        );
                      }
                      const actions = Array.isArray(notes.actionItems) ? notes.actionItems.slice(0, 3) : [];
                      const risks = Array.isArray(notes.risksAndIssues) ? notes.risksAndIssues.slice(0, 2) : [];

                      if (actions.length === 0 && risks.length === 0) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Nessuna azione o rischio principale disponibile nelle note AI.
                          </Typography>
                        );
                      }

                      return (
                        <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
                          {actions.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Prossime azioni
                              </Typography>
                              {actions.map((item, idx) => (
                                <Typography key={`it-action-${idx}`} variant="body2">
                                  • {item}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          {risks.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Rischi principali
                              </Typography>
                              {risks.map((item, idx) => (
                                <Typography key={`it-risk-${idx}`} variant="body2">
                                  • {item}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Paper>
                      );
                    })()}
                  </Box>
                )}

                {intelligenceTab === 'transcription' && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Trascrizione</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 260, overflowY: 'auto' }}>
                      {Array.isArray(intelligenceData.transcriptionSegments) && intelligenceData.transcriptionSegments.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {intelligenceData.transcriptionSegments.map((segment, idx) => (
                            <Box key={`${segment.start || idx}-${segment.caller || 'x'}`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                <Chip size="small" label={segment.speaker || 'Interlocutore'} />
                                <Typography variant="caption" color="text.secondary">
                                  {segment.start !== null && segment.start !== undefined ? `${Number(segment.start).toFixed(1)}s` : ''}
                                </Typography>
                              </Box>
                              <Typography variant="body2">{segment.text}</Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {intelligenceData.transcription || 'Trascrizione non disponibile'}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}

                {intelligenceTab === 'ai-notes' && (intelligenceData.smartNotes_it || intelligenceData.smartNotes) && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Note AI</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {Array.isArray((intelligenceData.smartNotes_it || intelligenceData.smartNotes).actionItems) && (intelligenceData.smartNotes_it || intelligenceData.smartNotes).actionItems.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Azioni da intraprendere</Typography>
                          {(intelligenceData.smartNotes_it || intelligenceData.smartNotes).actionItems.map((item, idx) => (
                            <Typography key={`ai-action-${idx}`} variant="body2">
                              • {item}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {Array.isArray((intelligenceData.smartNotes_it || intelligenceData.smartNotes).decisionsMade) && (intelligenceData.smartNotes_it || intelligenceData.smartNotes).decisionsMade.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Decisioni prese</Typography>
                          {(intelligenceData.smartNotes_it || intelligenceData.smartNotes).decisionsMade.map((item, idx) => (
                            <Typography key={`ai-decision-${idx}`} variant="body2">
                              • {item}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {Array.isArray((intelligenceData.smartNotes_it || intelligenceData.smartNotes).risksAndIssues) && (intelligenceData.smartNotes_it || intelligenceData.smartNotes).risksAndIssues.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Rischi / Issues</Typography>
                          {(intelligenceData.smartNotes_it || intelligenceData.smartNotes).risksAndIssues.map((item, idx) => (
                            <Typography key={`ai-risk-${idx}`} variant="body2">
                              • {item}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {Array.isArray((intelligenceData.smartNotes_it || intelligenceData.smartNotes).openQuestions) && (intelligenceData.smartNotes_it || intelligenceData.smartNotes).openQuestions.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Domande aperte</Typography>
                          {(intelligenceData.smartNotes_it || intelligenceData.smartNotes).openQuestions.map((item, idx) => (
                            <Typography key={`ai-question-${idx}`} variant="body2">
                              • {item}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {Array.isArray((intelligenceData.smartNotes_it || intelligenceData.smartNotes).customerFeedback) && (intelligenceData.smartNotes_it || intelligenceData.smartNotes).customerFeedback.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Feedback cliente</Typography>
                          {(intelligenceData.smartNotes_it || intelligenceData.smartNotes).customerFeedback.map((item, idx) => (
                            <Typography key={`ai-feedback-${idx}`} variant="body2">
                              • {item}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Box>
                )}

              </>
            ) : (
              <Typography>Nessun dato disponibile</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIntelligenceDialog}>Chiudi</Button>
          <Button
            variant="contained"
            onClick={() => intelligenceCall && openIntelligenceDialog(intelligenceCall)}
            disabled={!intelligenceCall || intelligenceLoading}
          >
            Aggiorna intelligence
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={privacyDialogOpen} onClose={closePrivacyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertTriangleIcon size={18} color="#ef4444" />
            <span>Conferma Privacy</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography>
              Stai per ascoltare una registrazione telefonica. Assicurati di avere un motivo legittimo e l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.
            </Typography>
            {privacyPendingCall ? (
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Direzione</TableCell>
                      <TableCell>Da</TableCell>
                      <TableCell>A</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionIcon direction={privacyPendingCall.direction} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {translateDirection(privacyPendingCall.direction)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {(() => {
                          const { daName } = getPrivacyNames(privacyPendingCall);
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                              <Typography variant="body2">{formatPhone(privacyPendingCall.from_number)}</Typography>
                              {daName ? (
                                <Typography variant="caption" color="text.secondary">{`(${daName})`}</Typography>
                              ) : null}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {(() => {
                          const { aName } = getPrivacyNames(privacyPendingCall);
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                              <Typography variant="body2">{formatPhone(privacyPendingCall.to_number)}</Typography>
                              {aName ? (
                                <Typography variant="caption" color="text.secondary">{`(${aName})`}</Typography>
                              ) : null}
                            </Box>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePrivacyDialog}>Annulla</Button>
          <Button onClick={confirmPrivacyDialog} variant="contained" autoFocus>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CloudTalkList;
